import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './app/router';
import { AuthProvider } from './hooks/useAuth';
import { AuthBootstrapEscape } from './components/auth/AuthBootstrapEscape';
import { GlobalActivityLoader } from './components/brand/GlobalActivityLoader';
import { PwaUpdatePrompt } from './components/pwa/PwaUpdatePrompt';
import { installImagePerformanceDefaults } from './lib/image-performance';
import { DEFAULT_STALE_TIME, QUERY_GC_TIME } from './lib/query-cache';
import './styles/globals.css';

// Stable entry-level deployment signature. Route code-splitting moves Landing
// copy into async chunks, so production verification must not depend on an
// eagerly imported page. These data attributes also make field diagnostics
// possible without loading any additional module.
document.documentElement.dataset.chaveaProduct = 'O melhor gerenciador de campeonatos de EA FC e e-Sports!';
document.documentElement.dataset.chaveaApi = import.meta.env.VITE_API_URL?.trim() ?? '';

installImagePerformanceDefaults();

// Entry-level update recovery for iOS standalone mode: unlike component
// effects, this runs even when the router is waiting for auth validation.
// A single shared check is throttled so connectivity/focus events do not
// cause redundant update() calls or multiple registrations.
if ('serviceWorker' in navigator) {
  let lastCheck = 0;
  let updatePending = false;
  const checkWorker = () => {
    if (!navigator.onLine || updatePending || Date.now() - lastCheck < 15_000) return;
    lastCheck = Date.now();
    updatePending = true;
    void navigator.serviceWorker.getRegistration('/').then((registration) =>
      registration?.update(),
    ).catch((error) => {
      console.warn('[pwa] entry update check failed', error);
    }).finally(() => {
      updatePending = false;
    });
  };
  window.addEventListener('online', checkWorker);
  window.addEventListener('pageshow', checkWorker);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkWorker();
  });
  // Defer until the first frame; VitePWA handles registration itself.
  window.requestAnimationFrame(checkWorker);
}


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      gcTime: QUERY_GC_TIME,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // Background refetches and query-key transitions must never blank the
      // current mobile screen. Keep the last successful payload interactive
      // until the replacement payload arrives.
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      retry: 0,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthBootstrapEscape />
        <RouterProvider router={router} />
      </AuthProvider>
      <GlobalActivityLoader />
      <PwaUpdatePrompt />
    </QueryClientProvider>
  </React.StrictMode>,
);
